import { useEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import CharacterAvatar from "./CharacterAvatar";
import { gameState } from "./gameStore";
import { SPAWN_POSITION, CORRIDOR_BOUNDS, POIS } from "./poi";
import { checkBuildingCollision } from "./buildingLayout";

const MOVE_SPEED = 8.5;
const TURN_SPEED = 2.1; // rad/sec
const RADIUS = 0.42;

// Classic "tank controls": W/S always move forward/backward along the
// character's CURRENT heading, A/D turn that heading left/right. There is
// only one source of truth for direction (the character's own heading) and
// the camera purely trails it — this removes any forward/backward ambiguity.
export default function CharacterController({ buildings, onActivePOIChange, onMove }) {
  const groupRef = useRef();
  const movingRef = useRef(false);
  const keysRef = useRef({});
  const headingRef = useRef(Math.PI);
  const lastActivePOI = useRef(null);
  const hasMovedRef = useRef(false);

  useEffect(() => {
    const down = (e) => {
      keysRef.current[e.key.toLowerCase()] = true;
    };
    const up = (e) => {
      keysRef.current[e.key.toLowerCase()] = false;
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;
    const keys = keysRef.current;

    const turnInput =
      (keys["a"] || keys["arrowleft"] ? 1 : 0) - (keys["d"] || keys["arrowright"] ? 1 : 0);
    const moveInput =
      (keys["s"] || keys["arrowdown"] ? 1 : 0) - (keys["w"] || keys["arrowup"] ? 1 : 0);

    const clampedTurn = THREE.MathUtils.clamp(turnInput, -1, 1);
    const clampedMove = THREE.MathUtils.clamp(moveInput, -1, 1);
    const isMoving = Math.abs(clampedMove) > 0.01;
    movingRef.current = isMoving || Math.abs(clampedTurn) > 0.01;

    if (Math.abs(clampedTurn) > 0.01) {
      headingRef.current += clampedTurn * TURN_SPEED * delta;
    }
    group.rotation.y = headingRef.current;

    if (isMoving) {
      const forwardX = Math.sin(headingRef.current);
      const forwardZ = Math.cos(headingRef.current);
      const nextX = group.position.x + forwardX * clampedMove * MOVE_SPEED * delta;
      const nextZ = group.position.z + forwardZ * clampedMove * MOVE_SPEED * delta;

      if (!checkBuildingCollision(nextX, group.position.z, RADIUS, buildings)) {
        group.position.x = THREE.MathUtils.clamp(nextX, CORRIDOR_BOUNDS.minX, CORRIDOR_BOUNDS.maxX);
      }
      if (!checkBuildingCollision(group.position.x, nextZ, RADIUS, buildings)) {
        group.position.z = THREE.MathUtils.clamp(nextZ, CORRIDOR_BOUNDS.minZ, CORRIDOR_BOUNDS.maxZ);
      }

      if (!hasMovedRef.current) {
        hasMovedRef.current = true;
        onMove?.();
      }
    }

    gameState.position.copy(group.position);
    gameState.facing = headingRef.current;
    gameState.moving = isMoving;

    let nearest = null;
    for (const poi of POIS) {
      const [px, , pz] = poi.position;
      const dist = Math.hypot(group.position.x - px, group.position.z - pz);
      if (dist <= poi.radius) {
        nearest = poi.id;
        break;
      }
    }
    if (nearest !== lastActivePOI.current) {
      lastActivePOI.current = nearest;
      onActivePOIChange?.(nearest);
    }
  });

  return (
    <group ref={groupRef} position={SPAWN_POSITION} rotation={[0, Math.PI, 0]} data-testid="game-character">
      <CharacterAvatar movingRef={movingRef} />
    </group>
  );
}
